import {
  Alert,
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { StyledPaper } from '../../shared/Styled/Styled';
import { LoadingButton } from '@mui/lab';
import { Save } from '@mui/icons-material';
import { object, SchemaOf, string } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { CustomTextFieldInput } from '../../shared/CustomTextFieldInput/CustomTextFieldInput';
import { AutocompleteOff } from '../CampaignsPage/shared';
import {
  allProductMenusCacheKey,
  getSingleProductMenu,
  importFoodoraCatalogToShops,
  upsertProductMenu,
  useAllIngredients,
  useAllProducts,
  useConciseProductMenu,
  useDefaultProductMenu,
  useFoodoraProductMenu,
} from '../../apis/products-api';
import { ProductMenuDto, ShopProductMenuDto } from '@kotipizzagroup/kotipizza-products-api-client';
import { SearchInput } from '../../shared/Search/SearchInput';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCustomSnackBar } from '../../shared/SnackBar/useCustomSnackBar';
import { postToSlack } from '../../apis/utils';
import { FormSwitch } from '../../shared/FormSwitch/FormSwitch';
import { useAllRestaurantExternalSystems, useAllRestaurants } from '../../apis/shops-api';
import { getProductsApiError } from '../../apis/products-api.errors';

const formSchema: SchemaOf<ProductMenuDto> = object()
  .shape({
    name: string().required(),
    description: string().nullable(),
  })
  .defined();

const ProductMenuDetails = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isImportingFoodoraMenu, setIsImportingFoodoraMenu] = useState(false);
  const [shops, setShops] = useState<ShopProductMenuDto[]>([]);
  const [productIds, setProductIds] = useState<number[]>([]);
  const [ingredientIds, setIngredientIds] = useState<number[]>([]);

  const { id } = useParams<{ id?: string }>();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const snackBar = useCustomSnackBar();
  const navigate = useNavigate();
  const upsertProductMenuMutation = useMutation(upsertProductMenu);

  const { data: defaultMenu, isLoading: isDefaultMenuLoading } = useDefaultProductMenu();
  const { data: conciseMenu, isLoading: isConciseMenuLoading } = useConciseProductMenu();
  const { data: products, isLoading: isProductsLoading } = useAllProducts();
  const { data: ingredients, isLoading: isIngredientsLoading } = useAllIngredients();
  const { data: restaurants, isLoading: isRestaurantsLoading } = useAllRestaurants();
  const { data: externalSystems } = useAllRestaurantExternalSystems();
  const { data: foodoraProductMenu } = useFoodoraProductMenu();

  const isNewMenu = useMemo(() => isNaN(Number(id)), [id]);
  const isFoodoraMenu = useMemo(() => foodoraProductMenu?.productMenuId === Number(id), [id, foodoraProductMenu]);

  useEffect(() => {
    setIsLoading(
      isDefaultMenuLoading || isProductsLoading || isRestaurantsLoading || isConciseMenuLoading || isIngredientsLoading
    );
  }, [isDefaultMenuLoading, isProductsLoading, isRestaurantsLoading, isConciseMenuLoading, isIngredientsLoading]);

  useEffect(() => {
    if (!defaultMenu || !conciseMenu || isNewMenu) return;

    if (defaultMenu.productMenuId === Number(id) || conciseMenu.productMenuId === Number(id)) {
      setIsReadOnly(true);
    }
  }, [conciseMenu, defaultMenu, id, isNewMenu]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProductMenuDto>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      isActive: false,
    },
  });

  useEffect(() => {
    if (isNewMenu || !id) return;

    const setInitialMenu = async () => {
      const selectedMenu = await getSingleProductMenu(Number(id));
      if (selectedMenu) {
        reset(selectedMenu);
        setShops(selectedMenu.shopsHavingMenu || []);
        setProductIds(selectedMenu.productIds || []);
        setIngredientIds(selectedMenu.ingredientIds || []);
      }
    };

    setInitialMenu();
  }, [isNewMenu, id, reset]);

  const handleAddRestaurant = (value: string) => {
    if (shops.find((shop) => shop.shopExternalId === value)) return;

    const shopToAdd: ShopProductMenuDto = {
      shopExternalId: value,
      availableForDelivery: true,
      availableForEatIn: true,
      availableForPickup: true,
      productMenuId: Number(id) || -1,
      shopProductMenuId: 0,
    };

    setShops((curr) => [...curr, shopToAdd]);
  };

  const handleAddProduct = (value: string) => {
    if (productIds.find((productId) => productId === Number(value))) return;
    setProductIds((curr) => [...curr, Number(value)]);
  };

  const handleAddIngredient = (value: string) => {
    if (ingredientIds.find((ingredientId) => ingredientId === Number(value))) return;
    setIngredientIds((curr) => [...curr, Number(value)]);
  };

  const handleProductMenuSave: SubmitHandler<ProductMenuDto> = async (formData) => {
    try {
      const payload: ProductMenuDto = {
        ...formData,
        shopsHavingMenu: shops,
        productIds,
        ingredientIds,
      };

      // Save product menu
      const response = await upsertProductMenuMutation.mutateAsync(payload);
      await queryClient.invalidateQueries([allProductMenusCacheKey]);

      if (!payload.productMenuId) {
        snackBar.showSuccess('Uusi tuotevalikoima luotu');
        postToSlack(`Product menu ${payload.name} created.`, 'product-updates');
        navigate(`/product-menus/${response.id}`);
      } else {
        snackBar.showSuccess('Tuotevalikoima päivitetty');
        postToSlack(`Product menu ${payload.name} edited from Juuri.`, 'product-updates');
        navigate('/product-menus');
      }
    } catch (e) {
      const apiError = getProductsApiError(e);
      snackBar.showError(apiError.message);
    }
  };

  const updateFoodoraMenuToAllRestaurants = async () => {
    setIsImportingFoodoraMenu(true);
    if (!externalSystems) {
      setIsImportingFoodoraMenu(false);
      snackBar.showError(t('restaurant.externalSystems.foodoraMenuExport.errorMessage'));
      return;
    }

    const foodoraRestaurantsShopExternalIds = externalSystems.restaurantExternalSystems
      .filter((x) => x.foodoraVendorId !== undefined)
      .map((x) => x.shopExternalId);

    if (!foodoraRestaurantsShopExternalIds || foodoraRestaurantsShopExternalIds.length < 1) {
      setIsImportingFoodoraMenu(false);
      snackBar.showError(t('restaurant.externalSystems.foodoraMenuExport.errorMessage'));
      return;
    }

    try {
      await importFoodoraCatalogToShops({
        shopExternalIds: foodoraRestaurantsShopExternalIds,
      });
      snackBar.showSuccess(t('restaurant.externalSystems.foodoraMenuExport.successMessage'));
    } catch {
      snackBar.showError(t('restaurant.externalSystems.foodoraMenuExport.errorMessage'));
    } finally {
      setIsImportingFoodoraMenu(false);
    }
  };

  const SaveButton = () => {
    return (
      <LoadingButton
        onClick={handleSubmit(handleProductMenuSave)}
        startIcon={<Save />}
        loading={isLoading}
        disabled={isLoading || isReadOnly}
        variant="contained"
        color="primary"
      >
        {t('global.save')}
      </LoadingButton>
    );
  };

  return (
    <Box>
      {isReadOnly && <Alert severity="warning">{t('productMenus.details.defaultMenuWarning')}</Alert>}
      <TopBar>
        <Typography variant="h1">
          {t('productMenus.list.title')} / {isNewMenu ? t('productMenus.addMenu') : t('productMenus.editMenu')}
        </Typography>
        <SaveButton />
      </TopBar>
      <Grid item xs={12} width={800} my={1}>
        <Typography variant="body1" mb={2}>
          <Trans i18nKey="productMenus.details.information1" components={{ bold: <strong /> }} />
        </Typography>
        <Typography variant="body1" mb={2}>
          {t('productMenus.details.information2')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <StyledPaper>
          <Typography variant="h2">{t('productMenus.details.information')}</Typography>
          <FormSwitch
            name="isActive"
            control={control}
            label={t('productDetails.active')}
            uncheckedLabel={t('productDetails.inactive')}
            disabled={isReadOnly}
          />
          <CustomTextFieldInput
            name="name"
            label={t('productMenus.list.name')}
            control={control}
            isDirty={isDirty}
            error={errors['name']}
            readOnly={isReadOnly}
            textFieldProps={{
              inputProps: AutocompleteOff,
              rows: 1,
              multiline: true,
              margin: 'normal',
            }}
          />
          <CustomTextFieldInput
            name="description"
            label={t('productMenus.list.description')}
            control={control}
            isDirty={isDirty}
            error={errors['description']}
            readOnly={isReadOnly}
            textFieldProps={{
              inputProps: AutocompleteOff,
              rows: 2,
              multiline: true,
              margin: 'normal',
            }}
          />
        </StyledPaper>
      </Grid>
      {isFoodoraMenu && (
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="h2" mb={2}>
              {t('productMenus.foodora.title')}
            </Typography>
            <Typography variant="body1" mb={2}>
              {t('productMenus.foodora.description')}
            </Typography>
            <LoadingButton
              onClick={updateFoodoraMenuToAllRestaurants}
              loading={isImportingFoodoraMenu}
              disabled={!externalSystems}
              variant="contained"
              color="primary"
            >
              {t('productMenus.foodora.exportMenu')}
            </LoadingButton>
          </StyledPaper>
        </Grid>
      )}
      <Grid container item justifyContent="space-between" alignItems="center" my={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="h2">{t('Restaurants')}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          {!isReadOnly && (
            <SearchInput
              values={restaurants?.map((r) => ({ value: r.shopExternalId, label: r.displayName })) || []}
              onSelect={handleAddRestaurant}
              label="ravintola"
            />
          )}
        </Grid>
      </Grid>
      <Grid item xs={12} my={2}>
        <TableContainer component={Paper}>
          <LinearProgress sx={{ visibility: isRestaurantsLoading ? 'visible' : 'hidden' }} />
          <Table>
            <TableBody>
              {shops.map((shopInMenu) => (
                <TableRow key={shopInMenu.shopExternalId}>
                  <TableCell>
                    <PositiveQuantityText>
                      {restaurants?.find((r) => r.shopExternalId === shopInMenu.shopExternalId)?.displayName}
                    </PositiveQuantityText>
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => {
                        const shopIndex = shops.indexOf(shopInMenu);
                        const curr = [...shops];
                        curr[shopIndex].availableForDelivery = !curr[shopIndex].availableForDelivery;
                        setShops(curr);
                      }}
                    >
                      <Typography
                        sx={{ color: shopInMenu.availableForDelivery ? 'success.main' : 'error.main', fontWeight: 700 }}
                        variant="body2"
                      >
                        {shopInMenu.availableForDelivery ? 'Kuljetus' : 'Ei kuljetusta'}
                      </Typography>
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => {
                        const shopIndex = shops.indexOf(shopInMenu);
                        const curr = [...shops];
                        curr[shopIndex].availableForPickup = !curr[shopIndex].availableForPickup;
                        setShops(curr);
                      }}
                    >
                      <Typography
                        sx={{ color: shopInMenu.availableForPickup ? 'success.main' : 'error.main', fontWeight: 700 }}
                        variant="body2"
                      >
                        {shopInMenu.availableForPickup ? 'Nouto' : 'Ei noutoa'}
                      </Typography>
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => {
                        const shopIndex = shops.indexOf(shopInMenu);
                        const curr = [...shops];
                        curr[shopIndex].availableForEatIn = !curr[shopIndex].availableForEatIn;
                        setShops(curr);
                      }}
                    >
                      <Typography
                        sx={{ color: shopInMenu.availableForEatIn ? 'success.main' : 'error.main', fontWeight: 700 }}
                        variant="body2"
                      >
                        {shopInMenu.availableForEatIn ? 'Paikanpäällä' : 'Ei paikanpäällä'}
                      </Typography>
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    {!isReadOnly && (
                      <Button
                        onClick={() => {
                          setShops((curr) => curr.filter((shop) => shop.shopExternalId !== shopInMenu.shopExternalId));
                        }}
                      >
                        {t('global.delete')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid container item justifyContent="space-between" alignItems="center">
        <Grid item xs={12} md={4}>
          <Typography variant="h2">{t('Products')}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          {!isReadOnly && (
            <SearchInput
              values={
                products
                  ?.filter((p) => p.productId && !defaultMenu?.productIds?.includes(p.productId))
                  .map((p) => ({ value: p.productId?.toString() || '', label: p.name })) || []
              }
              onSelect={handleAddProduct}
              label={t('product')}
            />
          )}
        </Grid>
      </Grid>
      <Grid item xs={12} my={2}>
        <TableContainer component={Paper}>
          <LinearProgress sx={{ visibility: isProductsLoading ? 'visible' : 'hidden' }} />
          <Table>
            <TableBody>
              {productIds.map((productId) => (
                <TableRow key={productId}>
                  <TableCell>
                    <PositiveQuantityText>
                      {products?.find((p) => p.productId === productId)?.name}
                    </PositiveQuantityText>
                  </TableCell>
                  <TableCell align="right">
                    {!isReadOnly && (
                      <Button
                        onClick={() => {
                          setProductIds(productIds.filter((pId) => pId !== productId));
                        }}
                      >
                        {t('global.delete')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid container item justifyContent="space-between" alignItems="center">
        <Grid item xs={12} md={4}>
          <Typography variant="h2">{t('ingredients.title')}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          {!isReadOnly && (
            <SearchInput
              values={ingredients?.map((p) => ({ value: p.ingredientId?.toString() || '', label: p.name })) || []}
              onSelect={handleAddIngredient}
              label={t('ingredient')}
            />
          )}
        </Grid>
      </Grid>

      <Grid item xs={12} my={2}>
        <TableContainer component={Paper}>
          <LinearProgress sx={{ visibility: isProductsLoading ? 'visible' : 'hidden' }} />
          <Table>
            <TableBody>
              {ingredientIds.map((ingredientId) => (
                <TableRow key={`ingredient-${ingredientId}`}>
                  <TableCell>
                    <PositiveQuantityText>
                      {ingredients?.find((i) => i.ingredientId === ingredientId)?.name}
                    </PositiveQuantityText>
                  </TableCell>
                  <TableCell align="right">
                    {!isReadOnly && (
                      <Button
                        onClick={() => {
                          setIngredientIds(ingredientIds.filter((iId) => iId !== ingredientId));
                        }}
                      >
                        {t('global.delete')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <SaveButton />
      <BotBar />
    </Box>
  );
};

const BotBar = styled.div`
  height: 100px;
  width: 100%;
`;

const PositiveQuantityText = styled(Typography).attrs({ variant: 'body2', color: 'primary', fontWeight: 700 })``;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 1rem 0;
`;

export default ProductMenuDetails;
